import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setGuideOpen(false);
    setCatOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-4">
        <Link to="/" className="font-heading text-xl md:text-2xl font-bold text-foreground-900 whitespace-nowrap cursor-pointer">
          ITAOBUY
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-5">
          <Link
            to="/"
            className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            Home
          </Link>

          {/* Guida Shopping dropdown */}
          <div className="relative" ref={guideRef}>
            <button
              onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
              className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              Guida Shopping
              {guideOpen ? (
                <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center"></i>
              ) : (
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              )}
            </button>
            {guideOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-lg p-2 shadow-sm border border-background-200/70 min-w-[240px] z-50">
                {[
                  { label: 'Guida Completa', path: '/articolo-itaobuy-spreadsheet-guida' },
                  { label: 'Sicurezza e Privacy', path: '/articolo-sicurezza-itaobuy-spreadsheet' },
                  { label: 'Confronto Alternative', path: '/articolo-confronto-itaobuy-spreadsheet' },
                  { label: 'Tutorial Pratico', path: '/articolo-tutorial-itaobuy-spreadsheet' },
                ].map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setGuideOpen(false)}
                    className="block text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 px-3 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Categorie dropdown */}
          <div className="relative" ref={catRef}>
            <button
              onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
              className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
            >
              Categorie
              {catOpen ? (
                <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center"></i>
              ) : (
                <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
              )}
            </button>
            {catOpen && (
              <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-lg p-3 shadow-sm border border-background-200/70 min-w-[300px] z-50 grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 px-2 py-1.5 rounded-md transition-colors cursor-pointer"
                  >
                    <span className="flex-shrink-0 w-8 h-8 rounded-md overflow-hidden bg-background-200">
                      <img src={cat.image} alt={cat.nameIt} className="w-full h-full object-cover" />
                    </span>
                    <span className="whitespace-nowrap">{cat.nameIt}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="bg-primary-500 text-background-50 px-4 py-2 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            itaobuy sheet
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer text-foreground-900"
        >
          {mobileOpen ? (
            <i className="ri-close-line w-6 h-6 flex items-center justify-center"></i>
          ) : (
            <i className="ri-menu-line w-6 h-6 flex items-center justify-center"></i>
          )}
        </button>
      </div>

      {/* Mobile slide-out */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 shadow-sm max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            <Link
              to="/"
              onClick={closeAll}
              className="block text-sm font-semibold text-foreground-900 py-2.5 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              Home
            </Link>

            {/* Guida Shopping mobile */}
            <div>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="w-full text-left text-sm font-semibold text-foreground-900 py-2.5 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-between"
              >
                <span>
                  <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
                  Guida Shopping
                </span>
                {guideOpen ? (
                  <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center"></i>
                ) : (
                  <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
                )}
              </button>
              {guideOpen && (
                <div className="ml-4 space-y-1 border-l-2 border-background-200/70 pl-3">
                  {[
                    { label: 'Guida Completa', path: '/articolo-itaobuy-spreadsheet-guida' },
                    { label: 'Sicurezza e Privacy', path: '/articolo-sicurezza-itaobuy-spreadsheet' },
                    { label: 'Confronto Alternative', path: '/articolo-confronto-itaobuy-spreadsheet' },
                    { label: 'Tutorial Pratico', path: '/articolo-tutorial-itaobuy-spreadsheet' },
                  ].map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeAll}
                      className="block text-sm text-foreground-600 py-2 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Categorie mobile */}
            <div>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="w-full text-left text-sm font-semibold text-foreground-900 py-2.5 hover:text-primary-500 transition-colors cursor-pointer flex items-center justify-between"
              >
                <span>
                  <i className="ri-grid-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
                  Categorie
                </span>
                {catOpen ? (
                  <i className="ri-arrow-up-s-line w-4 h-4 flex items-center justify-center"></i>
                ) : (
                  <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
                )}
              </button>
              {catOpen && (
                <div className="ml-4 grid grid-cols-1 gap-0.5 border-l-2 border-background-200/70 pl-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeAll}
                      className="flex items-center gap-2 text-sm text-foreground-600 py-1.5 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <span className="flex-shrink-0 w-7 h-7 rounded overflow-hidden bg-background-200">
                        <img src={cat.image} alt={cat.nameIt} className="w-full h-full object-cover" />
                      </span>
                      <span>{cat.nameIt}</span>
                      <span className="text-foreground-400 text-xs ml-auto">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="block text-sm font-semibold text-primary-500 py-2.5 cursor-pointer"
            >
              <i className="ri-shopping-cart-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              itaobuy sheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}